import { ContentAction, ContentLocalizationStatus, type ContentVersionDTO } from '@today/types'

/**
 * Review, publish and retire dialogs (TO-420 content-admin-ui REQ-005/REQ-006).
 * The shared action schema accepts a null reason (the service enforces it per
 * action), so the reason and the approve window are checked here (design D2).
 */

export interface ApproveValues {
	reason: string
	/** `<input type="datetime-local">` value, read as UTC. */
	validFrom: string
	validTo: string
}

export interface PublishPreview {
	immediate: boolean
	/** ISO instant the localization goes into force: `max(validFrom, now)`. */
	effectiveStart: string
	/** The version whose same-language publication this one replaces, if any. */
	replaces: number | null
}

export const REASON_MAX = 2000

/** The reason rule: 1–2000 characters after trimming. */
export const reasonError = (reason: string): string | null => {
	const trimmed = reason.trim()
	if (!trimmed) return 'validation.reasonRequired'
	return trimmed.length > REASON_MAX ? 'validation.reasonLength' : null
}

/** A `datetime-local` value (`YYYY-MM-DDTHH:mm[:ss]`) as a UTC ISO instant, or null. */
export const toUtcIso = (value: string): string | null => {
	if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2})?$/.test(value)) return null
	const full = value.length === 16 ? `${value}:00` : value
	const date = new Date(`${full}Z`)
	// Date rolls a calendar-invalid value forward (Feb 30 → Mar 2); refuse it instead.
	return Number.isNaN(date.getTime()) || date.toISOString().slice(0, 19) !== full ? null : date.toISOString()
}

/** `{ action: "approve", reason, valid_from, valid_to? }`; `valid_to` is omitted when empty. */
export const approveBody = (values: ApproveValues): Record<string, unknown> => {
	const validTo = toUtcIso(values.validTo)
	return {
		action: ContentAction.APPROVE,
		reason: values.reason.trim(),
		valid_from: toUtcIso(values.validFrom),
		...(validTo ? { valid_to: validTo } : {}),
	}
}

/** Field → message key of the approve dialog; `{}` means it may be sent. */
export const approveErrors = (values: ApproveValues): Record<string, string> => {
	const errors: Record<string, string> = {}
	const reason = reasonError(values.reason)
	if (reason) errors.reason = reason
	const from = toUtcIso(values.validFrom)
	const to = toUtcIso(values.validTo)
	if (!from) errors.validFrom = 'validation.validFromRequired'
	else if (values.validTo && (!to || to <= from)) errors.validTo = 'validation.windowOrder'
	return errors
}

/** The body of submit / publish (no reason) and reject / retire (trimmed reason). */
export const actionBody = (
	action: ContentAction.SUBMIT | ContentAction.PUBLISH | ContentAction.REJECT | ContentAction.RETIRE,
	reason?: string,
): Record<string, unknown> =>
	action === ContentAction.SUBMIT || action === ContentAction.PUBLISH
		? { action }
		: { action, reason: (reason ?? '').trim() }

const time = (iso: string) => new Date(iso).getTime()

/**
 * The publish dialog: the effective start `max(validFrom, now)` and the version
 * whose same-language publication is in force or scheduled (the newest one).
 */
export const publishPreview = (
	loc: { id: string; validFrom: string | null },
	locale: string,
	versions: readonly ContentVersionDTO[],
	now: Date,
): PublishPreview => {
	const start = loc.validFrom && time(loc.validFrom) > now.getTime() ? new Date(loc.validFrom) : now
	const live = versions
		.filter((v) =>
			v.localizations.some(
				(l) =>
					l.id !== loc.id &&
					l.locale === locale &&
					l.status === ContentLocalizationStatus.PUBLISHED &&
					(l.validTo === null || time(l.validTo) > now.getTime()),
			),
		)
		.map((v) => v.versionNumber)
	return {
		immediate: start === now,
		effectiveStart: start.toISOString(),
		replaces: live.length ? Math.max(...live) : null,
	}
}

/** A published tab: in force since `from` (until `to`), or scheduled from `from`. */
export const publishedState = (
	loc: { validFrom: string | null; validTo: string | null },
	now: Date,
): { scheduled: boolean; from: string | null; to: string | null } => ({
	scheduled: loc.validFrom !== null && time(loc.validFrom) > now.getTime(),
	from: loc.validFrom,
	to: loc.validTo,
})

/** Which lifecycle buttons render for a localization (TO-420 content-admin-ui REQ-001/REQ-004…REQ-006). */
export interface LifecycleActions {
	submit: boolean
	submitDisabled: boolean
	approve: boolean
	reject: boolean
	pullBack: boolean
	publish: boolean
	retire: boolean
	newVersion: boolean
}

export const lifecycleActions = (args: {
	status: ContentLocalizationStatus
	write: boolean
	publish: boolean
	requiresApproval: boolean
	approver: boolean
	pullBack: boolean
	dirty: boolean
}): LifecycleActions => {
	const { status, write } = args
	const S = ContentLocalizationStatus
	return {
		submit: write && status === S.DRAFT && args.requiresApproval,
		submitDisabled: args.dirty,
		approve: args.approver,
		reject: args.approver,
		pullBack: args.pullBack,
		publish: args.publish && (status === S.APPROVED || (status === S.DRAFT && !args.requiresApproval)),
		retire: args.publish && (status === S.APPROVED || status === S.PUBLISHED),
		newVersion: write && (status === S.PUBLISHED || status === S.RETIRED),
	}
}
